import React from "react";
import { Briefcase, Mail, Phone, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContactOptionPreviewProps {
  label: string;
  email?: string;
  phone?: string;
  title?: string;
  status?: string;
  companyName?: string;
  /** "responsive" (default) collapses metadata to a row at >=md. "vertical" stays stacked. */
  layout?: "responsive" | "vertical";
}

export const getContactStatusColor = (status?: string): string => {
  switch (status) {
    case "customer":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    case "prospect":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "lead":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200";
    case "churned":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    case "inactive":
      return "bg-card-100 text-gray-800 dark:text-gray-200";
    default:
      return "bg-card-100 text-gray-800 dark:text-gray-200";
  }
};

export function ContactOptionPreview({
  label,
  email,
  phone,
  title,
  status,
  companyName,
  layout = "responsive",
}: ContactOptionPreviewProps): React.ReactNode {
  const hasMetadata = Boolean(companyName || title || email || phone);
  const metadataWrapperClass =
    layout === "vertical"
      ? "flex flex-col gap-1 text-xs text-muted-foreground"
      : "grid grid-cols-1 gap-1 text-xs text-muted-foreground md:flex md:items-center md:gap-3";

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium truncate">{label}</span>
        {status && (
          <span className={cn("text-xs px-2 py-0.5 rounded-full", getContactStatusColor(status))}>
            {status}
          </span>
        )}
      </div>
      {hasMetadata && (
        <div className={metadataWrapperClass}>
          {companyName && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <Briefcase className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{companyName}</span>
            </div>
          )}
          {title && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <User className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{title}</span>
            </div>
          )}
          {email && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{email}</span>
            </div>
          )}
          {phone && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <Phone className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{phone}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
